import { useCallback } from 'react';

// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
import { v4 as uuid } from 'uuid';

import { fg } from '@atlaskit/platform-feature-flags/fg';

import { useAnalyticsEvents } from '../../../common/analytics/generated/use-analytics-events';
import { getMeasure } from '../../../utils/get-measure';
import { mark } from '../../../utils/mark';
import { failUfoExperience } from '../../analytics/failUfoExperience';
import { startUfoExperience } from '../../analytics/startUfoExperience';
import { succeedUfoExperience } from '../../analytics/succeedUfoExperience';
import { type InvokeClientActionHandler, type UseInvokeClientActionProps } from './types';

const ACTION_EXPERIENCE_NAME = 'smart-link-action-invocation';

/**
 * Invoke client action such as preview, download and open link
 */
const useInvokeClientAction = ({
	fireEvent: fireEventProp,
}: UseInvokeClientActionProps): InvokeClientActionHandler => {
	const { fireEvent: defaultFireEvent } = useAnalyticsEvents();
	const fireEvent = fireEventProp ?? defaultFireEvent;

	return useCallback(
		async ({
			actionSubjectId,
			actionType,
			actionFn,
			definitionId = null,
			extensionKey,
			display,
			id,
			prompt,
			interactionSource,
			resourceType = null,
		}) => {
			// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
			const experienceId = uuid();

			// Begin performance instrumentation.
			const markName = `${experienceId}-${actionType}`;
			mark(markName, 'pending');

			try {
				// Begin UFO experience
				startUfoExperience(ACTION_EXPERIENCE_NAME, experienceId, {
					actionType,
					display,
					extensionKey,
					invokeType: 'client',
				});

				// Begin analytics instrumentation.
				if (actionSubjectId !== undefined) {
					fireEvent(`ui.button.clicked.${actionSubjectId}`, {
						actionType: actionType ?? null,
						definitionId,
						display: display ?? null,
						id: id ?? experienceId,
						resourceType,
						prompt,
						...(fg('platform_sl_one_click_chat_spotlight_v2_fg') &&
						actionSubjectId === 'rovoChatPrompt' &&
						interactionSource
							? { interactionSource }
							: {}),
					});
				}
				// Invoke action
				const result = await actionFn();

				mark(markName, 'resolved');
				succeedUfoExperience(ACTION_EXPERIENCE_NAME, experienceId);
				fireEvent('operational.smartLinkAction.resolved', {
					actionType: actionType ?? null,
					definitionId,
					display: display ?? null,
					duration: getMeasure(markName, 'resolved')?.duration ?? null,
					id: id ?? experienceId,
					resourceType,
				});

				return result;
			} catch (err) {
				mark(markName, 'errored');
				failUfoExperience(ACTION_EXPERIENCE_NAME, experienceId);
				const reason = typeof err === 'string' ? err : (err as any)?.message;

				fireEvent('operational.smartLinkAction.unresolved', {
					actionType: actionType ?? null,
					definitionId,
					display: display ?? null,
					duration: getMeasure(markName, 'errored')?.duration ?? null,
					id: id ?? experienceId,
					reason,
					resourceType,
				});
			}
		},
		[fireEvent],
	);
};

export default useInvokeClientAction;
